import * as React from 'react';
import { I18n } from 'react-redux-i18n';
import { Modal, ModalBody, ModalFooter, ModalHeader, ModalTitle } from '@common/components/modal';
import { getTestAttributes } from '@common/utils/tests';
import { Flex } from '@common/components/Flex';
import { Button } from '@common/components/Button';

import './profile-selector.styles.scss';

// TODO use this component in Individuals

export type ProfileSelectorItem = {
  id: string;
  name: string;
};

export type ProfileSelectorProps = {
  onClose: () => void;
  onSubmit: (selectedItems: Array<ProfileSelectorItem>) => Promise<void>;
  showErrorMessage: (msg: string) => void; 
  getData: () => Promise<Array<ProfileSelectorItem>>;
};

export const ProfileSelector: React.FC<ProfileSelectorProps> = (props) => {
  const { getData, onClose, onSubmit, showErrorMessage } = props;
  const [list, setList] = React.useState<Array<ProfileSelectorItem>>([]);
  const [value, setValue] = React.useState<Array<string>>([]);
  const [submitting, setSubmitting] = React.useState(false);

  React.useEffect(() => {
    getData()
      .then(setList)
      .catch((err: unknown) => {
        console.error(err);
        showErrorMessage(I18n.t('Failed to get profiles'));
      });
  }, []);

  const handleChange = React.useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const id = e.currentTarget.dataset.id as string;

    setValue((prevValue) => prevValue.includes(id)
      ? prevValue.filter((x) => x !== id)
      : [...prevValue, id]
    );
  }, []);

  const renderCheckbox = React.useCallback(({ id }: ProfileSelectorItem, rowIndex: number) => (
    <input type="checkbox" {...getTestAttributes({ id: `profile-chooser-grid-${rowIndex + 1}-1` })} readOnly checked={value.includes(id)} />
  ), [value]);

  const columns = React.useMemo(() => [
    {
      key: 'name',
      width: '80%',
      field: 'name',
      name: I18n.t('Profile Name'),
    },
    {
      key: 'checkbox',
      render: renderCheckbox
    }
  ], [renderCheckbox]);

  const handleSubmit = React.useCallback(async () => {
    setSubmitting(true);

    try {
      const selectedItems = value.reduce<Array<ProfileSelectorItem>>((acc, x) => {
        const item = list.find(({ id }) => id === x);

        if (item) {
          acc.push(item);
        }

        return acc;
      }, []);
      await onSubmit(selectedItems);
    } catch (err) {
      console.error(err);
      setSubmitting(false);
      showErrorMessage(I18n.t('Failed to select profile'));
    }
  }, [value, onSubmit, showErrorMessage, list]);

  return (
    <Modal 
      show
      animation={false}
      bsSize="small"
      backdrop="static"
      bsClass="bootstrap modal"
      className="ProfileSelector"
      onEscapeKeyDown={onClose}
      onHide={onClose}
    >
      <ModalHeader closeButton onHide={onClose}>
        <ModalTitle {...getTestAttributes({ id: 'lbl-profile-chooser-header' })}>
          {I18n.t('Select Profile')}
        </ModalTitle>
      </ModalHeader>

      <ModalBody>
        <Flex justify="space-between" alignItems="center" className="ProfileSelector__list-header">
          {columns.map((x) => (
            <span key={x.key} style={{ width: x.width }}>{x.name}</span>
          ))}
        </Flex>
        <div className="ProfileSelector__list">
          {list.map((item, i) => (
            <Flex 
              justify="space-between" 
              alignItems="center" 
              key={item.id} 
              className="ProfileSelector__list-row"
              data-name={item.name}
              data-id={item.id}
              onClick={handleChange}
              {...getTestAttributes({ id: `profile-chooser-grid-${i + 1}-0` })}
            >
              {columns.map((col) => (
                <React.Fragment key={col.key}>
                  <span style={{ width: col.width }}>{col.render ? col.render(item, i) : (item as { [key: string]: string })[col.field]}</span>
                </React.Fragment>
              ))}
            </Flex>
          ))}
        </div>
      </ModalBody>
      <ModalFooter>
        <Button 
          disabled={submitting}
          {...getTestAttributes({ id: 'btn-profile-chooser-cancel' })}
          onClick={onClose}
        >
          {I18n.t('Cancel')}
        </Button>
        <Button 
          bsStyle="primary"
          disabled={!value.length || submitting}
          {...getTestAttributes({ id: 'btn-profile-chooser-save' })}
          onClick={handleSubmit}
        >
          {submitting ? I18n.t('Loading') : I18n.t('Apply')}
        </Button>
      </ModalFooter>
    </Modal>
  );
};